<template>
  <div class="hist-page">
    <!-- Header -->
    <div class="hist-head">
      <div class="hist-head__left">
        <div class="kicker">{{ portalKicker }}</div>
        <h2 class="title">History</h2>
        <p class="sub">
          Search and open any order you’re allowed to see. View payments, docs, and full event timeline.
        </p>
      </div>

      <div class="hist-head__right">
        <button class="btn" @click="refreshAll" :disabled="loadingList || loadingDetail">
          {{ (loadingList || loadingDetail) ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>
    </div>

    <div class="hist-grid">
      <!-- Left: List -->
      <div class="card list-card">
        <div class="card-head">
          <div>
            <div class="card-title">Orders</div>
            <div class="card-sub">
              Latest <span class="pill">{{ orders.length }}</span> (max 300)
            </div>
          </div>

          <div class="tools">
            <input
              v-model="q"
              class="inp"
              placeholder="Search name / phone / lead code / order id"
              @keyup.enter="loadOrders"
            />

            <select v-model="stage" class="inp inp--select" @change="loadOrders">
              <option value="">All Stages</option>
              <option v-for="s in stages" :key="s" :value="s">{{ s }}</option>
            </select>

            <button class="btn btn--soft" @click="loadOrders" :disabled="loadingList">
              {{ loadingList ? 'Loading…' : 'Search' }}
            </button>
          </div>
        </div>

        <div v-if="listError" class="alert alert--danger">{{ listError }}</div>

        <div class="list">
          <button
            v-for="o in orders"
            :key="o.id"
            class="item"
            :class="selected && selected.id === o.id ? 'item--active' : ''"
            @click="openOrder(o)"
          >
            <div class="it-top">
              <div class="it-id">#{{ o.id }}</div>
              <div class="it-stage">
                <span class="pill pill--stage">{{ (o.stage || '—').toUpperCase() }}</span>
              </div>
            </div>

            <div class="it-name">{{ o.customerName || 'Customer' }}</div>

            <div class="it-meta">
              <span class="chip">{{ o.leadCode || '—' }}</span>
              <span class="chip">{{ o.customerPhone || '—' }}</span>
            </div>

            <div class="it-time">Updated: {{ fmtDT(o.updatedAt || o.createdAt) }}</div>
          </button>

          <div v-if="!loadingList && orders.length === 0" class="empty">
            <div class="empty-title">No orders found</div>
            <div class="empty-sub">Try removing filters or searching a different keyword.</div>
          </div>
        </div>
      </div>

      <!-- Right: Detail -->
      
    </div>

  </div>
</template>

<script>
export default {
  name: 'History',
  data () {
    return {
      q: '',
      stage: '',
      stages: ['BM_PENDING','BM_REJECTED','BM_APPROVED','DISPATCH_PENDING','INSTALL_PENDING','CLOSED','CANCELLED'],

      orders: [],
      selected: null,

      detail: null,

      loadingList: false,
      loadingDetail: false,

      listError: '',
      detailError: ''
    }
  },

  computed: {
    user () {
      return this.$store.getters['auth/currentUser'] || {}
    },
    role () {
      return (this.user && this.user.role) ? this.user.role : ''
    },

    portalKicker () {
      if (this.role === 'finance') return 'Finance Portal'
      if (this.role === 'dispatch') return 'Dispatch Portal'
      if (this.role === 'installer') return 'Installer Portal'
      return 'Sales Portal'
    },

    order () {
      return (this.detail && this.detail.order) ? this.detail.order : {}
    },
    payments () {
      return (this.detail && Array.isArray(this.detail.payments)) ? this.detail.payments : []
    },
    docs () {
      return (this.detail && Array.isArray(this.detail.docs)) ? this.detail.docs : []
    },
    events () {
      return (this.detail && Array.isArray(this.detail.events)) ? this.detail.events : []
    },
    flags () {
      return (this.detail && this.detail.flags) ? this.detail.flags : { dispatchDone: false, installDone: false }
    },

    dispatchDocs () {
      return this.docs.filter(d => String(d.portal || '').toUpperCase() === 'DISPATCH')
    },
    installerDocs () {
      return this.docs.filter(d => String(d.portal || '').toUpperCase() === 'INSTALLER')
    },
    otherDocs () {
      return this.docs.filter(d => {
        const p = String(d.portal || '').toUpperCase()
        return p !== 'DISPATCH' && p !== 'INSTALLER'
      })
    }
  },

  created () {
    this.loadOrders()

    var open = this.$route && this.$route.query ? this.$route.query.open : null
    if (open) {
      var oid = parseInt(open, 10)
      if (!isNaN(oid) && oid > 0) {
        this.$router.replace({ name: 'sales-order-detail', params: { id: String(oid) } })
      }
    }
  },

  methods: {
    fmtDT (v) {
      if (!v) return '—'
      try {
        var d = new Date(v)
        if (isNaN(d.getTime())) return String(v)
        return d.toLocaleString()
      } catch (e) {
        return String(v)
      }
    },

    prettyBytes (n) {
      try {
        n = Number(n || 0)
        if (!n) return '0 B'
        var units = ['B', 'KB', 'MB', 'GB']
        var u = 0
        while (n >= 1024 && u < units.length - 1) { n = n / 1024; u++ }
        return (Math.round(n * 10) / 10) + ' ' + units[u]
      } catch (e) {
        return String(n || 0)
      }
    },

    openOrder (o) {
      if (!o || !o.id) return
      this.$router.push({ name: 'sales-order-detail', params: { id: String(o.id) } })
    },


    fmtMoney (n) {
      try {
        n = Number(n || 0)
        return n.toLocaleString('en-IN')
      } catch (e) {
        return String(n || 0)
      }
    },

    refreshAll () {
      this.loadOrders()
    },


    async loadOrders () {
      this.listError = ''
      this.loadingList = true
      try {
        // Backend supports: /api/orders?stage=...&q=...
        // We also allow typing "#123" or "123" to auto-open order detail directly.
        var qq = (this.q || '').trim()
        var numeric = null
        if (qq) {
          var m = qq.match(/^#?(\d+)$/)
          if (m && m[1]) numeric = parseInt(m[1], 10)
        }

        var params = {}
        if ((this.stage || '').trim()) params.stage = (this.stage || '').trim()
        if (qq && numeric === null) params.q = qq

        var res = await this.$api.get('/orders', { params: params })
        this.orders = Array.isArray(res && res.data) ? res.data : []

        // If user typed an order id, open it.
        if (numeric && numeric > 0) {
          // try to find in list; if not found, still open by id (access is enforced server-side)
          var found = this.orders.find(o => o.id === numeric) || { id: numeric, stage: '' }
          this.selectOrder(found)
        } else {
          // keep selection if still present
          if (this.selected && this.selected.id) {
            var keep = this.orders.find(o => o.id === this.selected.id)
            if (keep) this.selected = keep
          }
        }
      } catch (e) {
        this.listError = this.pickErr(e, 'Failed to load orders')
      } finally {
        this.loadingList = false
      }
    },

    async tryAutoOpen (oid) {
      try {
        await this.loadOrders()
        var found = this.orders.find(o => o.id === oid)
        if (found) {
          this.selectOrder(found)
        } else {
          this.selected = { id: oid, stage: '' }
          this.loadDetail(oid)
        }
      } catch (e) {}
    },

    selectOrder (o) {
      this.selected = o
      this.detail = null
      this.detailError = ''
      if (o && o.id) this.loadDetail(o.id)
    },

    async loadDetail (oid) {
      this.detailError = ''
      this.loadingDetail = true
      try {
        // Backend: GET /api/orders/<id> returns { order, payments, docs, events, flags }
        var res = await this.$api.get('/orders/' + oid)
        this.detail = (res && res.data) ? res.data : null
      } catch (e) {
        this.detailError = this.pickErr(e, 'Failed to load order detail')
      } finally {
        this.loadingDetail = false
      }
    },

    async downloadDoc (d) {
      if (!d || !d.id) return
      try {
        var token = window.localStorage.getItem('edda_token')
        var res = await fetch('/api/orders/docs/' + d.id + '/download', {
          method: 'GET',
          headers: { Authorization: 'Bearer ' + token }
        })
        if (!res.ok) throw new Error('http_' + res.status)

        var blob = await res.blob()
        var a = document.createElement('a')
        var url = window.URL.createObjectURL(blob)
        a.href = url
        a.download = d.fileName || ('order_doc_' + d.id)
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.detailError = 'Download failed'
      }
    },

    async copyOrderId (oid) {
      try {
        await navigator.clipboard.writeText(String(oid))
      } catch (e) {
        // ignore
      }
    },

    pickErr (e, fallback) {
      try {
        if (e && e.payload && e.payload.error) return e.payload.error
        if (e && e.response && e.response.data && e.response.data.error) return e.response.data.error
        if (e && e.message) return e.message
      } catch (ex) {}
      return fallback || 'failed'
    }
  }
}
</script>

<style scoped>
.hist-page { padding: 18px; }
.hist-head { display:flex; justify-content:space-between; gap:14px; align-items:flex-end; margin-bottom:14px; }
.kicker { font-size:12px; opacity:.7; }
.title { margin:0; font-size:22px; }
.sub { margin:6px 0 0; opacity:.75; font-size:13px; max-width:720px; }

.hist-single { width: 100%; }
.list { max-height: calc(100vh - 240px); overflow:auto; }
.item { width: 100%; }

@media (max-width: 980px) { .hist-grid { grid-template-columns: 1fr; } }

.card { background:#fff; border:1px solid rgba(0,0,0,.08); border-radius:14px; box-shadow:0 6px 20px rgba(0,0,0,.04); overflow:hidden; }
.card-head { padding:14px; border-bottom:1px solid rgba(0,0,0,.06); display:flex; justify-content:space-between; gap:12px; align-items:center; }
.card-title { font-weight:800; }
.card-sub { font-size:12px; opacity:.75; margin-top:4px; }

.tools { display:flex; gap:8px; align-items:center; flex-wrap:wrap; justify-content:flex-end; }

.inp { width: 320px; max-width: 100%; }

.inp { border:1px solid rgba(0,0,0,.12); border-radius:10px; padding:10px 10px; font-size:13px; outline:none; min-width: 220px; }
.inp--select { min-width: 170px; }
@media (max-width: 980px) {
  .inp { min-width: 0; width: 100%; }
  .tools { width: 100%; }
}

.btn { border:1px solid rgba(0,0,0,.12); background:#fff; padding:10px 12px; border-radius:10px; cursor:pointer; font-size:13px; }
.btn:disabled { opacity:.55; cursor:not-allowed; }
.btn--soft { background: rgba(0,0,0,.03); }
.btn--primary { background:#111; color:#fff; border-color:#111; }

.alert { margin:12px; padding:10px 12px; border-radius:10px; font-size:13px; }
.alert--danger { background: rgba(255,0,0,.06); border:1px solid rgba(255,0,0,.15); }

.list { padding:10px; display:flex; flex-direction:column; gap:8px; max-height: calc(100vh - 240px); overflow:auto; }
.item { text-align:left; border:1px solid rgba(0,0,0,.08); background:#fff; border-radius:12px; padding:10px; cursor:pointer; }
.item:hover { border-color: rgba(0,0,0,.18); }
.item--active { border-color:#111; box-shadow: 0 0 0 3px rgba(0,0,0,.08); }

.it-top { display:flex; justify-content:space-between; align-items:center; gap:10px; }
.it-id { font-weight:900; }
.it-name { margin-top:6px; font-weight:800; }
.it-meta { margin-top:6px; display:flex; gap:6px; flex-wrap:wrap; }
.chip { font-size:12px; opacity:.75; background: rgba(0,0,0,.04); padding:4px 8px; border-radius:999px; }
.it-time { margin-top:8px; font-size:12px; opacity:.65; }

.pill { display:inline-block; font-size:12px; padding:3px 8px; border-radius:999px; background: rgba(0,0,0,.06); }
.pill--stage { background: rgba(0,0,0,.08); font-weight:800; }

.detail-actions { display:flex; gap:8px; align-items:center; }

.summary { padding:14px; display:grid; grid-template-columns: 1fr 1fr; gap:10px 14px; }
@media (max-width: 980px) { .summary { grid-template-columns: 1fr; } }
.row { display:flex; justify-content:space-between; gap:10px; border:1px solid rgba(0,0,0,.06); border-radius:12px; padding:10px; background: rgba(0,0,0,.02); }
.label { font-size:12px; opacity:.75; }
.value { font-weight:800; }

.section { padding:14px; border-top:1px solid rgba(0,0,0,.06); }
.section-head { margin-bottom:10px; }
.section-title { font-weight:900; }
.section-sub { margin-top:4px; font-size:12px; opacity:.75; }

.table { border:1px solid rgba(0,0,0,.08); border-radius:12px; overflow:hidden; }
.trow { display:grid; grid-template-columns: 160px 140px 120px 180px 1fr; gap:10px; padding:10px; align-items:center; border-top:1px solid rgba(0,0,0,.06); }
.trow:first-child { border-top:none; }
.thead { background: rgba(0,0,0,.03); font-size:12px; opacity:.9; font-weight:900; }
.note { opacity:.85; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }

.badge { display:inline-flex; align-items:center; padding:4px 8px; border-radius:999px; font-size:12px; font-weight:900; }
.badge.ok { background: rgba(0,200,120,.12); color:#0a7; border:1px solid rgba(0,200,120,.25); }
.badge.bad { background: rgba(255,0,0,.08); color:#c22; border:1px solid rgba(255,0,0,.18); }

.doc-split { display:grid; grid-template-columns: 1fr 1fr; gap:12px; }
@media (max-width: 980px) { .doc-split { grid-template-columns: 1fr; } }
.doc-col { border:1px solid rgba(0,0,0,.08); border-radius:14px; padding:12px; background: rgba(0,0,0,.02); }
.doc-col__title { font-weight:900; display:flex; justify-content:space-between; align-items:center; margin-bottom:10px; }

.docs-list { display:flex; flex-direction:column; gap:8px; }
.doc-row { display:flex; justify-content:space-between; align-items:center; gap:10px; border:1px solid rgba(0,0,0,.08); border-radius:12px; padding:10px; background:#fff; }
.doc-title { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
.doc-pill { font-size:12px; background: rgba(0,0,0,.06); padding:4px 8px; border-radius:999px; font-weight:800; }
.doc-name { font-weight:800; }
.doc-meta { margin-top:4px; font-size:12px; opacity:.7; display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.dot { opacity:.5; }

.other-docs { margin-top:12px; }

.events { display:flex; flex-direction:column; gap:10px; }
.evt { border:1px solid rgba(0,0,0,.08); border-radius:14px; padding:10px; background: rgba(0,0,0,.02); }
.evt-top { display:flex; gap:8px; align-items:center; }
.evt-type { font-weight:900; font-size:12px; padding:3px 8px; border-radius:999px; background:#fff; border:1px solid rgba(0,0,0,.08); }
.evt-time { font-size:12px; opacity:.75; }
.evt-body { margin-top:6px; }
.evt-note { opacity:.9; }
.evt-by { margin-top:6px; }

.empty { padding:18px; text-align:center; opacity:.85; }
.empty-title { font-weight:900; }
.empty-sub { margin-top:6px; font-size:13px; opacity:.75; }
.muted { opacity:.7; font-size:13px; }
</style>
